import { toISODate } from './crmDates'

// A WON opportunity (a "Current Vehicle") reshaped into the quote object
// QuoteCard renders, so a recorded sale reads like the deal it came from.
// selected_deal_detail — the Deal that MarkSold pinned — is the only
// trustworthy link back to a worksheet, so it alone supplies the card's id.
// The opportunity's other quotes_detail entries are ambiguous leftovers and
// must never become the click target.

const toDate = (raw) => {
    if (!raw) return null
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
}

// Historic sales kept the payment as free text: "$540/mo", "$1,250", "540",
// "n/a". Anything without a real number reads as unknown, not NaN.
const parseMonthlyPayment = (raw) => {
    if (raw === null || raw === undefined) return null
    const digits = String(raw).replace(/[^0-9.]/g, '')
    if (!digits) return null
    const n = Number(digits)
    return Number.isFinite(n) && n > 0 ? n : null
}

// Whole months between the two dates, a partial final month rounding down.
// Incomplete or backwards dates give no term rather than a misleading one.
export const getTermMonths = (inceptionDate, maturityDate) => {
    const start = toDate(inceptionDate)
    const end = toDate(maturityDate)
    if (!start || !end) return null
    const months = (end.getFullYear() - start.getFullYear()) * 12
        + (end.getMonth() - start.getMonth())
        - (end.getDate() < start.getDate() ? 1 : 0)
    return months > 0 ? months : null
}

// The pinned Deal, and only the pinned Deal — what gives the details link
// something real to point at.
const getSelectedDealId = (opportunity) => opportunity?.selected_deal_detail?.id ?? null

// The sale's own record decides the type: Close Deal and Edit Deal write it,
// and imported sales had it backfilled from their pinned Deal (crm 0015). The
// Deal only stands in for a row that somehow carries none.
export const getDealType = (opportunity) =>
    opportunity?.deal_type || opportunity?.selected_deal_detail?.deal_type || null

const PARTS = ['year', 'make', 'model', 'trim']
const hasParts = (details) => PARTS.some((key) => details?.[key])

// The sold car as the sale recorded it, then the pinned deal's car, else blank.
// sold_vehicle alone is never split: it is a label, not parts.
export const soldVehicleParts = (opportunity) => {
    const details = opportunity?.sold_vehicle_details
    const car = hasParts(details) ? details : opportunity?.car_detail || {}
    return Object.fromEntries(PARTS.map((key) => [key, car[key] != null ? String(car[key]) : '']))
}

// The sold car's name, one rule everywhere (the backend's too): the recorded
// parts, then the recorded label, then the pinned deal's car.
export const getSoldVehicleLabel = (opportunity) => {
    const details = opportunity?.sold_vehicle_details
    if (hasParts(details)) return PARTS.map((key) => details[key]).filter(Boolean).join(' ')
    return opportunity?.sold_vehicle || opportunity?.selected_deal_detail?.description || ''
}

// The card's title and spec line. Recorded parts split as the deal's do; a
// recorded label already holds its trim, so it stands alone — unless it is
// just the pinned car's own parts (Close Deal's default), which keep the
// deal's split.
const getCardName = (opportunity) => {
    const details = opportunity?.sold_vehicle_details
    if (hasParts(details)) {
        return {
            description: [details.year, details.make, details.model].filter(Boolean).join(' ') || null,
            spec: details.trim || null,
        }
    }
    const deal = opportunity?.selected_deal_detail
    const car = opportunity?.car_detail
    const dealCar = car ? PARTS.map((key) => car[key]).filter(Boolean).join(' ') : ''
    const label = opportunity?.sold_vehicle
    if (label && label !== dealCar && label !== deal?.description) return { description: label, spec: null }
    return { description: deal?.description || null, spec: deal?.spec ?? null }
}

// Add Car's ownership options have no CASH; a cash sale is owned outright.
// The card and the Edit Car modal both read ownership through this one map, so
// a sale can't say CASH in one place and OWNED_OUTRIGHT in the other.
const OWNERSHIP_BY_DEAL_TYPE = { LEASE: 'LEASE', FINANCE: 'FINANCE', CASH: 'OWNED_OUTRIGHT' }
const DETAILS_LABEL = { LEASE: 'Lease Details', FINANCE: 'Finance Details', CASH: 'Purchase Details' }

// The sticker price of the car that was sold. The pinned worksheet priced it,
// so its MSRP stands; an imported sale keeps its own only where no worksheet
// has one. A zero is not a sticker price — Deal.msrp defaults to 0.00.
const getMsrp = (opportunity) => {
    const priced = opportunity?.selected_deal_detail?.msrp
    if (Number(priced) > 0) return priced
    return opportunity?.msrp ?? null
}

// A realized sale reports what it actually made; only when nothing was
// realized does the worksheet's projection stand in.
const getProfit = (opportunity) => {
    const realized = Number(opportunity?.total_profit)
    if (Number.isFinite(realized) && realized > 0) return opportunity.total_profit
    return opportunity?.selected_deal_detail?.profit ?? null
}

// Current Vehicles report where the term stands instead of a quote's "Active".
export const getVehicleStatus = (maturityDate) => {
    const end = toDate(maturityDate)
    if (!end) return { label: 'Active', expired: false }
    const expired = end.getTime() < Date.now()
    const when = end.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    return { label: `${expired ? 'Expired' : 'Expires'} ${when}`, expired }
}

// Quote values come from the pinned Deal where it has them; the sale record
// fills the gaps. Missing fields stay null so QuoteCard shows its own
// placeholder rather than an invented number.
export const wonOppToQuote = (opportunity) => {
    const deal = opportunity?.selected_deal_detail || null

    return {
        id: getSelectedDealId(opportunity),
        ...getCardName(opportunity),
        lender: deal?.lender || opportunity?.dealership || null,
        // Ownership, in the vocabulary a car is held in rather than the one a
        // worksheet was desked in.
        deal_type: OWNERSHIP_BY_DEAL_TYPE[getDealType(opportunity)] || null,
        msrp: getMsrp(opportunity),
        due: deal?.due ?? null,
        payment: deal?.payment ?? parseMonthlyPayment(opportunity?.monthly_payment),
        term: deal?.term ?? getTermMonths(opportunity?.inception_date, opportunity?.maturity_date),
        miles: deal?.miles ?? null,
        profit: getProfit(opportunity),
        vin: opportunity?.vin || null,
        // The car's own term, not the lender program's window
        // (selected_deal_detail.expiration_date), which says nothing about a
        // car that was already delivered.
        exp: toISODate(opportunity?.maturity_date) || null,
        // Only what Edit Car recorded on the sale: `miles` above is the lease's
        // ANNUAL ALLOWANCE, and the pinned deal's trade_payoff belongs to the
        // car the customer traded in.
        odo: opportunity?.current_mileage ?? null,
        payoff: opportunity?.payoff ?? null,
    }
}

// A sold car as AddCarModal's vehicle. Y/M/M/T are the recorded parts, else the
// pinned deal's car (sold_vehicle is never split); odometer, payoff and notes
// are the sale's own (mileage_allowance is not an odometer). No `id`: it is
// not a CrmVehicle.
export const wonOppToVehicle = (opportunity) => {
    const car = soldVehicleParts(opportunity)
    const dealType = getDealType(opportunity)
    const dealId = getSelectedDealId(opportunity)

    return {
        vin: opportunity?.vin || '',
        year: car.year,
        make: car.make,
        model: car.model,
        trim: car.trim,
        ownership_type: OWNERSHIP_BY_DEAL_TYPE[dealType] || '',
        expiration_date: toISODate(opportunity?.maturity_date),
        mileage: opportunity?.current_mileage ?? null,
        payoff: opportunity?.payoff ?? null,
        notes: opportunity?.vehicle_notes || '',
        dealId,
        detailsLabel: dealId != null ? DETAILS_LABEL[dealType] || null : null,
    }
}

// A sale's trade-in as AddCarModal's vehicle, read only from the snapshot the
// sale took (trade_in_vehicle_details). A separate purchase recorded before the
// snapshot existed still has its own VIN; nothing else is filled in for it.
export const tradeInToVehicle = (opportunity) => {
    const car = opportunity?.trade_in_vehicle_details || {}
    const legacyVin = opportunity?.is_vehicle_purchase ? opportunity.vin : ''

    return {
        vin: car.vin || legacyVin || '',
        year: car.year ?? '',
        make: car.make || '',
        model: car.model || '',
        trim: car.trim || '',
        ownership_type: car.ownership_type || '',
        expiration_date: car.expiration_date || '',
        mileage: car.mileage ?? null,
        payoff: car.payoff ?? null,
        notes: car.notes || '',
    }
}
